import { useState } from "react";
import { X, Camera, Loader2, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PhotoUploader } from "@/components/PhotoUploader";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Reusable trip-inspection photo capture dialog for both guests and hosts.
// stage: host_pre | customer_start | customer_return | host_return
export function TripPhotosDialog({ open, onClose, endpoint, stage, min = 4, existing = [], onDone }) {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState(existing || []);
  const [saving, setSaving] = useState(false);
  if (!open) return null;

  const submit = async () => {
    if (photos.length < min) { toast.error(t("tripPhotos.needMin", { min })); return; }
    setSaving(true);
    try {
      await api.post(endpoint, { stage, images: photos });
      toast.success(t("tripPhotos.saved"));
      onDone?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="trip-photos-dialog">
      <div className="bg-white rounded-2xl shadow-card-hover w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-blue-600 text-white"><Camera size={17} /></span>
            <div>
              <h2 className="font-heading text-lg font-bold text-slate-900">{t(`tripPhotos.${stage}.title`)}</h2>
              <p className="text-xs text-slate-400">{t(`tripPhotos.${stage}.hint`, { min })}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="trip-photos-close" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-xl p-3.5 text-sm text-blue-800">
            <ShieldCheck size={16} className="mt-0.5 shrink-0" />
            <span>{t("tripPhotos.retention")}</span>
          </div>
          <PhotoUploader value={photos} onChange={setPhotos} min={min} />
          <div className="flex justify-end gap-3 pt-1">
            <button onClick={onClose} className="border-2 border-slate-200 px-6 py-2.5 rounded-full font-semibold text-slate-700 hover:bg-slate-50 transition-colors">{t("common.cancel")}</button>
            <button onClick={submit} disabled={saving || photos.length < min} data-testid="trip-photos-submit"
              className="bg-blue-600 text-white px-7 py-2.5 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-40 flex items-center gap-2">
              {saving ? <><Loader2 size={16} className="animate-spin" /> {t("tripPhotos.saving")}</> : t("tripPhotos.submit")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
